import type { FC } from 'react'
import type { CartProduct } from '../../shopping-cart/provider/types'
import { useShoppingCartOpsDispatch } from '../../shopping-cart/ShoppingCartProvider'
import QuantitySelect from '../../shopping-cart/cart-page/QuantitySelect'
import ProductRow from './ProductRow'
import { CART_QUANTITY_MAXIMUM, toShoppingCartItems } from '../../shopping-cart/util/cart'
import { PurchaseCategory, PurchaseType } from '@g4g/graphql/src/shop/__generated__/globalTypes'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import RaritySelect from '../../shopping-cart/cart-page/RaritySelect'
import { getProductRemainingQuantity } from '@/shop/src/utils/shopItemUtils'
import ShopItemLink from '../ShopItemLink'

const ProductRowQuantityEdit: FC<{ product: CartProduct }> = ({ product }) => {
  const dispatch = useShoppingCartOpsDispatch()
  const remainingQuantity = getProductRemainingQuantity(product.product.limit)

  return (
    <>
      {remainingQuantity === 1 ? (
        <span className="text-base font-poppins">1</span>
      ) : (
        <QuantitySelect
          // -1 product has no limit and can be bought infinitely
          cartQuantity={remainingQuantity === -1 ? CART_QUANTITY_MAXIMUM : remainingQuantity}
          selected={product.totalQuantity}
          buttonClassName="p-2"
          inputClassName="w-16"
          onChange={(num) => {
            dispatch({
              type: 'add-to-cart',
              items: toShoppingCartItems({
                product: product.product,
                totalQuantity: num,
              }),
            })
          }}
        />
      )}
    </>
  )
}

const ProductRowConsumableEdit: FC<{ product: CartProduct }> = (props) => (
  <>
    {props.product.product.purchaseCategory === PurchaseCategory.Bundle ? (
      <ShopItemLink
        kebabOrShopItem={props.product.product}
        data-cy="shop-product-row-edit-bundle"
        className="flex items-center space-x-1 text-white/80 hover:text-white"
      >
        <PencilSquareIcon className="w-4 h-4" />
        <span className="text-base">Edit</span>
      </ShopItemLink>
    ) : (
      <ProductRowQuantityEdit product={props.product} />
    )}
  </>
)

const ProductRowSubscriptionEdit: FC<{ product: CartProduct }> = ({ product }) => (
  <RaritySelect cartProduct={product.product} />
)

const ProductRowEditable: FC<{
  product: CartProduct
  isRemovable?: boolean
  shine?: boolean
  variant?: 'cart' | 'history'
}> = (props) => (
  <ProductRow {...props}>
    {props.product.product.purchaseType === PurchaseType.Consumable ? (
      <ProductRowConsumableEdit product={props.product} />
    ) : (
      <ProductRowSubscriptionEdit product={props.product} />
    )}
  </ProductRow>
)

export default ProductRowEditable
